import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Phone, MessageSquare, Clock, Check, Lock } from 'lucide-react';
import { Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/Toast';
import { useAuth } from '@/shared/hooks/useAuth';
import { formatPhoneDisplay } from '@/shared/utils/validation';
import { strategyApi } from '../api/strategyApi';
import { strategyKeys } from '../queryKeys';
import styles from './VapiCallModal.module.css';

const CALLER_ID = '(650) 586-7939';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toLocalDatetimeInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function localInputToISO(value: string): string {
  const d = new Date(value);
  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hh = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const mm = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${value}:00${sign}${hh}:${mm}`;
}

function todayLocal(): string {
  return toLocalDatetimeInput(new Date()).slice(0, 10);
}

function tenMinsFromNow(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 10, 0, 0);
  return toLocalDatetimeInput(d);
}

// ─── Types ───────────────────────────────────────────────────────────────────

type CallStep = 'phone' | 'schedule' | 'confirmed';

interface VapiCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  defaultPhone?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function VapiCallModal({ isOpen, onClose, locationId, defaultPhone }: VapiCallModalProps) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<CallStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState(user?.phone ?? defaultPhone ?? '');
  const [phoneError, setPhoneError] = useState('');
  const [scheduleDate, setScheduleDate] = useState(() =>
    toLocalDatetimeInput(new Date()).slice(0, 10),
  );
  const [scheduleTime, setScheduleTime] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 10, 0, 0);
    return toLocalDatetimeInput(d).slice(11);
  });
  const [isScheduled, setIsScheduled] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPhoneNumber(user?.phone ?? defaultPhone ?? '');
    setPhoneError('');
    const combined = tenMinsFromNow();
    setScheduleDate(combined.slice(0, 10));
    setScheduleTime(combined.slice(11));
    setStep('phone');
    setIsScheduled(false);
  }, [isOpen, user?.phone, defaultPhone]);

  const { mutate: initiateCall, isPending } = useMutation({
    mutationFn: ({ scheduled }: { scheduled: boolean }) =>
      strategyApi.initiateVapiCall(
        locationId,
        phoneNumber,
        scheduled ? localInputToISO(`${scheduleDate}T${scheduleTime}`) : undefined,
      ),
    onSuccess: (_data, vars) => {
      setIsScheduled(vars.scheduled);
      setStep('confirmed');
      void queryClient.invalidateQueries({ queryKey: strategyKeys.vapiSession(locationId) });
    },
    onError: (err: unknown) => {
      showToast((err as Error).message ?? 'Failed to schedule call', 'error');
    },
  });

  const scheduleInPast =
    !!scheduleDate &&
    !!scheduleTime &&
    new Date(`${scheduleDate}T${scheduleTime}`).getTime() <= Date.now();

  function formatConfirmedTime() {
    if (!scheduleDate || !scheduleTime) return '';
    return new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  function closeModal() {
    onClose();
    setStep('phone');
  }

  function validateAndCall(scheduled: boolean) {
    if (!phoneNumber.trim()) {
      setPhoneError('Please enter a phone number.');
      return;
    }
    if (scheduled) {
      setStep('schedule');
    } else {
      initiateCall({ scheduled: false });
    }
  }

  const modalTitle = step === 'schedule' ? 'Schedule your call' : 'Have our assistant call you';

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      title={modalTitle}
      icon={
        <>
          <Phone size={17} strokeWidth={2} />
          <span className={styles.liveDot} aria-hidden="true" />
        </>
      }
      size="sm"
    >
      {step === 'phone' && (
        <>
          <div className={styles.expect}>
            <div className={styles.expectRow}>
              <span className={styles.expectIcon} aria-hidden="true">
                <Phone size={16} strokeWidth={2} />
              </span>
              <span className={styles.expectText}>
                <span className={styles.expectLabel}>You'll get a call from</span>
                <span className={styles.expectVal}>
                  <span className={styles.callerId}>{CALLER_ID}</span>{' '}
                  <span className={styles.valSub}>— save it so you don't miss us.</span>
                </span>
              </span>
            </div>
            <div className={styles.expectRow}>
              <span className={styles.expectIcon} aria-hidden="true">
                <MessageSquare size={16} strokeWidth={2} />
              </span>
              <span className={styles.expectText}>
                <span className={styles.expectLabel}>On the call</span>
                <span className={styles.expectVal}>
                  A few quick questions about your practice, brand, and competitors. Just answer
                  naturally — no prep needed.
                </span>
              </span>
            </div>
            <div className={styles.expectRow}>
              <span className={styles.expectIcon} aria-hidden="true">
                <Clock size={16} strokeWidth={2} />
              </span>
              <span className={styles.expectText}>
                <span className={styles.expectLabel}>How long</span>
                <span className={styles.expectVal}>About 3–5 minutes.</span>
              </span>
            </div>
            <div className={styles.expectRow}>
              <span className={styles.expectIcon} aria-hidden="true">
                <Check size={16} strokeWidth={2} />
              </span>
              <span className={styles.expectText}>
                <span className={styles.expectLabel}>Right after</span>
                <span className={styles.expectVal}>
                  Your answers appear here automatically, ready for you to review and confirm.
                </span>
              </span>
            </div>
          </div>

          <label className={styles.fieldLabel} htmlFor="vapi-phone">
            Number to call <span className={styles.req}>*</span>
          </label>
          <p className={styles.fieldHint}>Re-confirm or edit the number we should call.</p>
          <div className={styles.inputWrap}>
            <Phone size={17} strokeWidth={2} aria-hidden="true" />
            <input
              id="vapi-phone"
              type="tel"
              inputMode="tel"
              value={formatPhoneDisplay(phoneNumber)}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                setPhoneError('');
              }}
              onBlur={(e) => setPhoneNumber(formatPhoneDisplay(e.target.value))}
              placeholder="(555) 555-5555"
              className={styles.phoneInput}
            />
          </div>
          {phoneError ? (
            <p className={styles.errorText}>{phoneError}</p>
          ) : (
            <div className={styles.spacer} />
          )}

          <div className={styles.actions}>
            <button
              className={styles.btnPrimary}
              onClick={() => validateAndCall(false)}
              disabled={isPending}
            >
              <Phone size={17} strokeWidth={2} aria-hidden="true" />
              {isPending ? 'Scheduling…' : 'Call me now'}
            </button>
            <button
              className={styles.btnGhost}
              onClick={() => validateAndCall(true)}
              disabled={isPending}
            >
              <Clock size={17} strokeWidth={2} aria-hidden="true" />
              Call me later
            </button>
          </div>

          <button type="button" className={styles.cancelBtn} onClick={closeModal}>
            Cancel — I'll fill it in here instead
          </button>

          <div className={styles.footNote}>
            <Lock size={13} strokeWidth={2} aria-hidden="true" />
            Calls may be recorded. We'll ask for your okay first.
          </div>
        </>
      )}

      {step === 'schedule' && (
        <>
          <p className={styles.lede}>
            Choose when we should call you at <strong>{formatPhoneDisplay(phoneNumber)}</strong>.
          </p>

          <div className={styles.scheduleGrid}>
            <div>
              <div className={styles.fieldLabel}>Date</div>
              <input
                type="date"
                value={scheduleDate}
                min={todayLocal()}
                onChange={(e) => setScheduleDate(e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div>
              <div className={styles.fieldLabel}>Time</div>
              <input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className={styles.dateInput}
              />
            </div>
          </div>

          {scheduleInPast && <p className={styles.scheduleError}>Pick a time later than now.</p>}

          <div className={styles.actions}>
            <button
              className={styles.btnPrimary}
              onClick={() => initiateCall({ scheduled: true })}
              disabled={!scheduleDate || !scheduleTime || scheduleInPast || isPending}
            >
              {isPending ? 'Scheduling…' : 'Confirm time'}
            </button>
            <button
              className={styles.btnGhost}
              onClick={() => setStep('phone')}
              disabled={isPending}
            >
              Back
            </button>
          </div>
        </>
      )}

      {step === 'confirmed' && (
        <div className={styles.confirmed}>
          <div className={styles.confirmedIcon}>
            {isScheduled ? (
              <Clock size={22} strokeWidth={1.75} aria-hidden="true" />
            ) : (
              <Phone size={22} strokeWidth={1.75} aria-hidden="true" />
            )}
          </div>
          <div className={styles.confirmedTitle}>
            {isScheduled ? 'Call scheduled' : 'Calling you now'}
          </div>
          <p className={styles.confirmedText}>
            {isScheduled
              ? `We'll call ${formatPhoneDisplay(phoneNumber)} at ${formatConfirmedTime()}. You can keep going in the app in the meantime — nothing is blocked.`
              : `We're calling ${formatPhoneDisplay(phoneNumber)} now. Answer when it rings — our assistant will help fill in your strategy.`}
          </p>
          <div className={styles.actions}>
            <button className={styles.btnPrimary} onClick={closeModal}>
              Continue in the app →
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
